import { memo } from 'react'
import { cn } from '@/utils/cn'
import type { BikeLineBranding } from '@/data/bikeLineBranding'

type LockupSize = 'sm' | 'md' | 'lg'

interface SeriesBadgeProps {
  /** Roman-numeral series mark, e.g. "I" or "E-I" */
  series: string
  size?: LockupSize
  className?: string
}

const BADGE_SIZE: Record<LockupSize, { box: string; numeral: string }> = {
  sm: { box: 'h-6 min-w-6 px-1', numeral: 'text-sm' },
  md: { box: 'h-9 min-w-9 px-1.5', numeral: 'text-xl' },
  lg: { box: 'h-12 min-w-12 px-2 md:h-16 md:min-w-16 md:px-2.5', numeral: 'text-3xl md:text-4xl' },
}

/**
 * Gold-bordered series badge from the brand sheet — a skewed plate with a
 * gilded serif numeral ("I", "E-I").
 */
export const SeriesBadge = memo(function SeriesBadge({ series, size = 'md', className }: SeriesBadgeProps) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block shrink-0 -skew-x-12 bg-gradient-to-b from-gold-200 via-gold-500 to-gold-800 p-px',
        className,
      )}
    >
      <span className={cn('flex items-center justify-center bg-carbon-900', BADGE_SIZE[size].box)}>
        <span
          className={cn(
            'font-serif italic font-semibold leading-none bg-gradient-to-b from-gold-100 via-gold-400 to-gold-700 bg-clip-text text-transparent',
            BADGE_SIZE[size].numeral,
          )}
        >
          {series}
        </span>
      </span>
    </span>
  )
})

interface ModelLockupProps {
  branding: BikeLineBranding
  size?: LockupSize
  /** Show the small "BURTON BIKES" eyebrow above the mark */
  showEyebrow?: boolean
  /** Show the platform designation column right of the badge */
  showPlatform?: boolean
  /** Element used for the model mark — h1 on hero sections, span inside cards */
  as?: 'h1' | 'h2' | 'h3' | 'span'
  className?: string
}

const LOCKUP_SIZE: Record<
  LockupSize,
  { eyebrow: string; mark: string; slash: string; platform: string; gap: string }
> = {
  sm: {
    eyebrow: 'text-2xs tracking-[0.4em] mb-1.5',
    mark: 'text-2xl',
    slash: 'h-6',
    platform: 'text-2xs tracking-[0.2em]',
    gap: 'gap-2.5',
  },
  md: {
    eyebrow: 'text-2xs tracking-[0.5em] mb-2',
    mark: 'text-4xl md:text-5xl',
    slash: 'h-9',
    platform: 'text-2xs tracking-[0.25em]',
    gap: 'gap-3.5',
  },
  lg: {
    eyebrow: 'text-xs tracking-[0.6em] mb-3',
    mark: 'text-5xl md:text-7xl xl:text-8xl',
    slash: 'h-12 md:h-16',
    platform: 'text-2xs md:text-xs tracking-[0.3em]',
    gap: 'gap-4 md:gap-6',
  },
}

/**
 * Full bike-line brand lockup from the brand sheet:
 * BURTON BIKES eyebrow · slanted model mark · gold slash · series badge ·
 * platform designation.
 */
export const ModelLockup = memo(function ModelLockup({
  branding,
  size = 'md',
  showEyebrow = true,
  showPlatform = true,
  as: Mark = 'span',
  className,
}: ModelLockupProps) {
  const s = LOCKUP_SIZE[size]

  return (
    <div className={cn('flex flex-col items-start', className)}>
      {showEyebrow && (
        <p className={cn('font-display font-bold uppercase text-carbon-50/90', s.eyebrow)}>
          Burton <span className="text-titanium-400 font-medium">Bikes</span>
        </p>
      )}

      <div className={cn('flex items-center', s.gap)}>
        <Mark
          className={cn(
            'font-display font-extrabold italic [font-stretch:125%] uppercase leading-none tracking-tight text-carbon-50',
            s.mark,
          )}
        >
          {branding.mark}
        </Mark>

        <span aria-hidden className={cn('w-px -skew-x-12 bg-gradient-to-b from-transparent via-gold-500 to-transparent', s.slash)} />

        <SeriesBadge series={branding.series} size={size} />

        {showPlatform && (
          <span className="flex flex-col gap-0.5 pl-1">
            <span className={cn('font-mono font-medium uppercase text-gold-500', s.platform)}>
              {branding.platformShort}
            </span>
            <span className={cn('font-mono uppercase text-titanium-500', s.platform)}>
              {branding.platformLabel}
            </span>
          </span>
        )}
      </div>
    </div>
  )
})
